import AppEvents from 'lib/AppEvents';
import CurrencyService from 'lib/services/CurrencyService';
import EventService from 'lib/services/EventService';
import LogService from 'lib/services/LogService';
import NotificationService from 'lib/services/NotificationService';
import UserService from 'lib/services/UserService';
import { getFacebookAccountId, mapCampaignInstances } from 'lib/utils/adaya/FacebookUtils';
import { debug } from 'lib/utils/debug';
import DataService from 'services/data/DataService';
import FacebookService from 'services/providers/FacebookService';
import {
    DATE_FORMAT, last3, last30, last7, lastMonth, previous7, previousMonthLastYear, thisMonth, thisMonthLastYear, today, twoMonths, yesterday
} from 'utils/DateUtils';
import { getLogger } from 'utils/logging';
import { fb_adsets_last_7, fb_adsets_today_hourly, fb_adsets_yesterday, fb_campaigns_last_7, fb_campaigns_last_7_active, fb_campaigns_today_hourly, fb_campaigns_yesterday, fb_campaign_list_hourly, fb_insights_last_3, fb_insights_last_7, fb_insights_last_month, fb_insights_previous_7, fb_insights_previous_month_last_year, fb_insights_this_month, fb_insights_this_month_last_year, fb_insights_two_months } from '../CacheKeys';
import { DataStatus } from './IDataProvider';

const { log, warn } = getLogger('FacebookDataProvider', { color: 'blue', enabled: false });

const DISABLE_LOCAL_DATA = true;

class FacebookDataProvider implements IDataProvider {

    stopRequests = false;
    stopReason = undefined;
    accountId = undefined;
    needsConversion = undefined;
    convertFrom = undefined;
    concertTo = undefined;

    constructor() {
    }

    /*
    // Update all data. Returns cached version if it exists for the given date ranges.
    // updateIfCached = true means it will always ask for fresh data, even if it's already cached.
    */
    public updateData = async (user, updateCallback?, completeCallback?, updateIfCached = false, updateTodayIfCached = false) => {
        try {
            log('updateData()', updateIfCached, updateTodayIfCached, process.env.NODE_ENV);

            // temp for dev
            if (DISABLE_LOCAL_DATA || process.env.NODE_ENV == 'local') {
                log(`Facebook provider update disabled.`)
                if (completeCallback) {
                    completeCallback();
                }
                return;
            }

            if (this.stopRequests) {
                console.warn('FacebookDataProvider: Stopping requests. Reason: ' + this.stopReason)
                return;
            }

            this.accountId = getFacebookAccountId(user.activeAccount);
            const { needsConversion, convertFrom, convertTo } = await UserService.getCurrencyConversionStatus(user);
            if (needsConversion) {
                console.warn(`NOTICE: Facebook currency does not match Shopify's currency. Converting Facebook currency data from ${convertFrom} (Facebook) to ${convertTo} (Shopify).`);
            }

            this.needsConversion = needsConversion;
            this.convertFrom = convertFrom;
            this.convertTo = convertTo;

            await this.preloadProviderData(user, updateIfCached, updateTodayIfCached, updateCallback, completeCallback);

            await this.updateAllProviderData(user, updateIfCached, updateTodayIfCached, updateCallback, completeCallback);

            if (completeCallback) {
                completeCallback();
            }
        } catch (error) {
            console.log('Caught FacebookDataProvider.updateData error', error, '\nTrace:');
            throw error;
        }

    }

    // Updates only some initial data sets, so it's faster, maybe...
    public preloadProviderData = async (user, updateIfCached = false, updateTodayIfCached = false, updateCallback?, completeCallback?) => {
        try {
            debug(user, 'Preload facebook provider data.');
            const _last3 = last3();
            const _last7 = last7();

            // gets all fb adset data (ads insights for the given campaigns) for the given periods
            const getAllAdsetsInsightsData = async (campaignData) => {
                log('getAllAdsetsInsightsData', campaignData, updateIfCached, updateTodayIfCached);

                return await Promise.all([
                    // get all current adsets for today
                    FacebookService.getAdInsights(
                        this.accountId,
                        campaignData.find(d => d.id == fb_campaigns_today_hourly())?.data,
                        today().startOf('day').format(DATE_FORMAT),
                        today().endOf('day').format(DATE_FORMAT),
                        fb_adsets_today_hourly(),
                        !updateIfCached,
                        updateTodayIfCached
                    ).then(async data => {
                        data = data || [];
                        return Promise.resolve({ id: fb_adsets_today_hourly(), data });
                    }).catch(e => {
                        console.log('CAUGHT fb-adsets-today', e);
                        debug(user, 'Caught FacebookDataProvider error in getAdInsights.', { error: e });
                        Promise.reject(e)
                    })
                ]);
            }

            const getAllCampaignInsightsData = async () => {
                return await Promise.all([
                    // Get today campaign data
                    FacebookService.getInsightsData(
                        'campaign',
                        this.accountId,
                        today().startOf('day').format(DATE_FORMAT),
                        today().endOf('day').format(DATE_FORMAT),
                        1,
                        100,
                        !updateIfCached,
                        fb_campaigns_today_hourly(),
                        (pollResponse) => {},
                        updateTodayIfCached
                    ).then(data => {
                        data = mapCampaignInstances(data);
                        return Promise.resolve({ id: fb_campaigns_today_hourly(), data });
                    }).catch(e => {
                        debug(user, 'Caught FacebookDataProvider error in getInsightsData.', { error: e });
                        Promise.reject(e)
                    })
                ]);
            }

            // we use last 7 to get a longer range of "recent" campaigns...
            const getCampaignList = async () => {
                return await FacebookService.campaignsListQuery(
                    this.accountId,
                    _last7[0].format(DATE_FORMAT),
                    _last7[1].format(DATE_FORMAT),
                    (pollResponse) => {},
                    true,
                    fb_campaign_list_hourly()
                );
            }

            // now get all of these sets:
            Promise.all([

                getCampaignList()
                    .then(async r => {
                        await DataService.saveDataSource(fb_campaign_list_hourly(), r);
                    }),

                // Gets campaign list, then the insights for them:
                // We save adset and campaign insights separately for convenience.
                getAllCampaignInsightsData()
                    .then(async r => {
                        if (r) {
                            // TODO:? currency conversions show happen in a more programmatic/automated fashion, for any provider
                            // (ie. should be some filter-detection on the account. Perhaps the Providers/here are the best place for it.
                            if (this.needsConversion) {
                                r = await CurrencyService.convertDatasetCurrency(r, this.convertFrom, this.convertTo);
                            }

                            await DataService.saveDataSources(r);

                            let ai = await getAllAdsetsInsightsData(r);
                            if (ai) {
                                if (this.needsConversion) {
                                    ai = await CurrencyService.convertDatasetCurrency(ai, this.convertFrom, this.convertTo);
                                }
                                await DataService.saveDataSources(ai);
                                r = r.concat(ai);
                            }
                        }

                        // resolve with both
                        return Promise.resolve(r);
                    })

            ]).then(async r => {
                // let all = r.flat();

                // let e = { status: DataStatus.ALL_COMPLETE, data: all };

                // if (updateCallback) {
                //     updateCallback(e);
                // }

                // if (completeCallback) {
                //     completeCallback(all);
                // }
            }).catch(async (error) => {
                let fbStatus;

                // TODO: MOVE ALL THIS!

                if (error.code && error.message == '(#100) Missing permissions') {
                    this.stopRequests = true;
                    this.stopReason = error.message;
                    console.error('FB token is missing permissions!', error)
                    return
                    //EventService.dispatch(AppEvents.NEEDS_LOGOUT);
                } else if (error.code == 190 && error.error_subcode == 463) {
                    // todo: should only do this if the current user/account is the same as the FB account:
                    fbStatus = await FacebookService.getLoginStatus();

                    if (fbStatus.status == 'connected') {
                        // allow update of the token if it's not an alwaysMock account, and not mocking...
                        if (user.alwaysMock || FacebookService.hasMockAccessToken()) {
                            let error = "Cannot update the account Facebook token - Adaya user is not the original Facebook user.";
                            console.error(error);
                            this.stopRequests = true;
                            this.stopReason = error;
                            return;
                        }

                        // try to update the token...
                        await FacebookService.tryUpdateToken(fbStatus, user);
                    } else {
                        // try to login and get a new token...
                        if (!await FacebookService.login()) {
                            console.log('FB Login failed?');
                            return false;
                        } else {
                        }
                    }

                    // Now try to redo the update...
                    return this.updateData(user, updateCallback, completeCallback, updateIfCached, updateTodayIfCached);
                }

                console.log('Caught FacebookDataProvider error', error, '\nTrace:');

                let errMsg = (error.message ? error.message : error);s
                LogService.logError("User is not authenticated through Facebook. Error: " + errMsg, { error, fbStatus }, null, user);

                throw error;
            });

        } catch(error) {
            console.log('Caught AccountDataProvider.preloadProviderData error', error);
            debug(user, 'Caught FacebookDataProvider error in preloadProviderData.', { error });
            throw error;
        }
    }

    // Asks to update all account data sets.
    public updateAllProviderData = async (user, updateIfCached = false, updateTodayIfCached = false, updateCallback?, completeCallback?) => {
        try {

            const _previous7 = previous7();
            const _last3 = last3();
            const _last7 = last7();
            const _last30 = last30();
            const _thisMonth = thisMonth();
            const _lastMonth = lastMonth();
            const _twoMonths = twoMonths();
            const _thisMonthLastYear = thisMonthLastYear();
            const _previousMonthLastYear = previousMonthLastYear();

            const getAllAccountInsightsData = async () => {
                return await Promise.all([

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        _previous7[0].format(DATE_FORMAT),
                        _previous7[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_previous_7(),
                        //(update) => { console.log(`insights update`, fb_insights_previous_7(), update) },
                        updateIfCached,
                    ),

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        _last3[0].format(DATE_FORMAT),
                        _last3[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_last_3(),
                        //(update) => { console.log(`insights update`, fb_insights_last_3(), update) },
                        updateIfCached,
                    ),

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        _last7[0].format(DATE_FORMAT),
                        _last7[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_last_7(),
                        //(update) => { console.log(`insights update`, fb_insights_last_7(), update) },
                        updateIfCached,
                    ),

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        _thisMonth[0].format(DATE_FORMAT),
                        _thisMonth[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_this_month(),
                        //(update) => { console.log(`insights update`, fb_insights_this_month(), update) },
                        updateIfCached,
                    ),

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        _lastMonth[0].format(DATE_FORMAT),
                        _lastMonth[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_last_month(),
                        //(update) => { console.log(`insights update`, fb_insights_last_month(), update) },
                        updateIfCached
                    ),

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        // we pad the days so the metric calculations have extra data for the current day - a week, etc...
                        _twoMonths[0].format(DATE_FORMAT),
                        _twoMonths[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_two_months(),
                        //(update) => { console.log(`insights update`, fb_insights_two_months(), update) },
                        updateIfCached
                    ),

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        _thisMonthLastYear[0].format(DATE_FORMAT),
                        _thisMonthLastYear[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_this_month_last_year(),
                        //(update) => { console.log(`insights update`, fb_insights_this_month_last_year(), update) },
                        updateIfCached
                    ),

                    FacebookService.getInsightsData(
                        'account',
                        this.accountId,
                        _previousMonthLastYear[0].format(DATE_FORMAT),
                        _previousMonthLastYear[1].format(DATE_FORMAT),
                        1,
                        undefined,
                        true,
                        fb_insights_previous_month_last_year(),
                        //(update) => { console.log(`insights update`, fb_insights_previous_month_last_year(), update) },
                        updateIfCached
                    )
                ]);
            }

            // gets all fb adset data (ads insights for the given campaigns) for the given periods
            const getAllAdsetsInsightsData = async (campaignData) => {
                log('getAllAdsetsInsightsData', campaignData, updateIfCached, updateTodayIfCached);

                return await Promise.all([

                    // get all current adsets for yesterday
                    FacebookService.getAdInsights(
                        this.accountId,
                        campaignData.find(d => d.id == fb_campaigns_yesterday())?.data,
                        yesterday().startOf('day').format(DATE_FORMAT),
                        yesterday().endOf('day').format(DATE_FORMAT),
                        fb_adsets_yesterday(),
                        !updateIfCached,
                        updateTodayIfCached // also update yesterday if Refresh
                    ).then(async data => {
                        data = data || [];
                        return Promise.resolve({ id: fb_adsets_yesterday(), data });
                    }).catch(e => {
                        console.log('CAUGHT fb-adsets-yesterday', e);
                        Promise.reject(e)
                    }),

                    // get all current adsets for last 7 days
                    FacebookService.getAdInsights(
                        this.accountId,
                        campaignData.find(d => d.id == fb_campaigns_last_7())?.data,
                        _last7[0].format(DATE_FORMAT),
                        _last7[1].format(DATE_FORMAT),
                        fb_adsets_last_7(),
                        !updateIfCached,
                        updateIfCached
                    ).then(async data => {
                        data = data || [];
                        return Promise.resolve({ id: fb_adsets_last_7(), data });
                    }).catch(e => {
                        console.log('CAUGHT fb-adsets-last-7', e);
                        Promise.reject(e)
                    })

                ]);
            }

            const getAllCampaignInsightsData = async () => {

                return await Promise.all([

                    // Get last 7 campaign data, active campaigns only
                    FacebookService.getInsightsData(
                        'campaign',
                        this.accountId,
                        _last7[0].format(DATE_FORMAT),
                        _last7[1].format(DATE_FORMAT),
                        1,
                        7,
                        !updateIfCached,
                        fb_campaigns_last_7_active(),
                        (pollResponse) => {
                            if (updateCallback) {
                                updateCallback({
                                    status: DataStatus.PROGRESS,
                                    id: fb_campaigns_last_7_active(),
                                    progress: (pollResponse.async_percent_completion || 0) + '%'
                                });
                            }
                        },
                        updateIfCached,
                        true
                    ).then(data => {
                        data = mapCampaignInstances(data);
                        return Promise.resolve({ id: fb_campaigns_last_7_active(), data })
                    }).catch(e => Promise.reject(e)),

                    // Get last 7 campaign data, all campaigns
                    FacebookService.getInsightsData(
                        'campaign',
                        this.accountId,
                        _last7[0].format(DATE_FORMAT),
                        _last7[1].format(DATE_FORMAT),
                        1,
                        7,
                        !updateIfCached,
                        fb_campaigns_last_7(),
                        (pollResponse) => {
                            if (updateCallback) {
                                updateCallback({
                                    status: DataStatus.PROGRESS,
                                    id: fb_campaigns_last_7(),
                                    progress: (pollResponse.async_percent_completion || 0) + '%'
                                });
                            }
                        },
                        updateIfCached
                    ).then(data => {
                        data = mapCampaignInstances(data);
                        return Promise.resolve({ id: fb_campaigns_last_7(), data });
                    }).catch(e => Promise.reject(e)),

                    // Get yesterday campaign data
                    FacebookService.getInsightsData(
                        'campaign',
                        this.accountId,
                        yesterday().startOf('day').format(DATE_FORMAT),
                        yesterday().endOf('day').format(DATE_FORMAT),
                        1,
                        7,
                        !updateIfCached,
                        fb_campaigns_yesterday(),
                        (pollResponse) => {
                            if (updateCallback) {
                                // todo: can use this after we support multiple data update progresses at a time (in tandem with above call)
                                // updateCallback({
                                //     status: DataStatus.PROGRESS,
                                //     id: 'fb-today',
                                //     progress: (pollResponse.async_percent_completion || 0) + '%'
                                // });
                            }
                        },
                        updateTodayIfCached // also update yesterday if Refresh
                    ).then(data => {
                        //console.log('fb_campaigns_yesterday sees:' fb_campaigns_yesterday(), data);
                        data = mapCampaignInstances(data);
                        return Promise.resolve({ id: fb_campaigns_yesterday(), data });
                    }).catch(e => Promise.reject(e))
                ]);
            }

            // now get all of these sets:
            Promise.all([

                // gets just account-level insights:
                getAllAccountInsightsData()
                    .then(async r => {
                        let data;
                        if (r && r.length > 7) {
                            // all insights done

                            // Facebook is sending back 8 days for some weekly results, need to make sure it's 7:
                            if (r[0].length > 7) {
                                r[0].splice(7);
                            }

                            if (r[2].length > 7) {
                                r[2].splice(7);
                            }

                            data = [
                                { id: fb_insights_previous_7(), data: r[0] },
                                { id: fb_insights_last_3(), data: r[1] },
                                { id: fb_insights_last_7(), data: r[2] },
                                { id: fb_insights_this_month(), data: r[3] },
                                { id: fb_insights_last_month(), data: r[4] },
                                { id: fb_insights_two_months(), data: r[5] },
                                { id: fb_insights_this_month_last_year(), data: r[6] },
                                { id: fb_insights_previous_month_last_year(), data: r[7] },
                            ]

                            if (this.needsConversion) {
                                data = await CurrencyService.convertDatasetCurrency(data, this.convertFrom, this.convertTo);
                            }

                            // save these first so other pages can update:
                            //console.log('saving account insights data:', data);
                            await DataService.saveDataSources(data);
                        }
                        return Promise.resolve(data);
                    }),

                // Gets campaign list, then the insights for them:
                // We save adset and campaign insights separately for convenience.
                getAllCampaignInsightsData()
                    .then(async r => {
                        if (r) {
                            // TODO:? currency conversions show happen in a more programmatic/automated fashion, for any provider
                            // (ie. should be some filter-detection on the account. Perhaps the Providers/here are the best place for it.
                            if (this.needsConversion) {
                                r = await CurrencyService.convertDatasetCurrency(r, this.convertFrom, this.convertTo);
                            }

                            await DataService.saveDataSources(r);

                            let ai = await getAllAdsetsInsightsData(r);
                            if (ai) {
                                if (this.needsConversion) {
                                    ai = await CurrencyService.convertDatasetCurrency(ai, this.convertFrom, this.convertTo);
                                }
                                await DataService.saveDataSources(ai);
                                r = r.concat(ai);
                            }
                        }

                        // resolve with both
                        return Promise.resolve(r);
                    })

            ]).then(async r => {
                let all = r.flat();

                let e = { status: DataStatus.ALL_COMPLETE, data: all };
                if (updateCallback) {
                    updateCallback(e);
                }

                if (completeCallback) {
                    completeCallback(all);
                }
            }).catch(async (error) => {
                let fbStatus;

                if (error.code && error.message == '(#100) Missing permissions') {
                    this.stopRequests = true;
                    this.stopReason = error.message;
                    console.error('FB token is missing permissions!', error)
                    return
                    //EventService.dispatch(AppEvents.NEEDS_LOGOUT);
                }  else if (error.code == 190 && error.error_subcode == 463) {
                    // todo: should only do this if the current user/account is the same as the FB account:
                    fbStatus = await FacebookService.getLoginStatus();

                    if (fbStatus.status == 'connected') {
                        // allow update of the token if it's not an alwaysMock account, and not mocking...
                        if (user.alwaysMock || FacebookService.hasMockAccessToken()) {
                            let error = "Cannot update the account Facebook token - Adaya user is not the original Facebook user.";
                            console.error(error);
                            this.stopRequests = true;
                            this.stopReason = error;
                            return;
                        }

                        // try to update the token...
                        await FacebookService.tryUpdateToken(fbStatus, user);
                    } else {
                        // try to login and get a new token...
                        if (!await FacebookService.login()) {
                            console.log('FB Login failed?');
                            return false;
                        } else {
                        }
                    }

                    // Now try to redo the update...
                    return this.updateData(user, updateCallback, completeCallback, updateIfCached, updateTodayIfCached);
                }

                console.log('Caught FacebookDataProvider error', error, '\nTrace:');

                let errMsg = (error.message ? error.message : error);
                NotificationService.notifyError('Facebook Error', errMsg);
                LogService.logError("User is not authenticated through Facebook. Error: " + errMsg, { error, fbStatus }, null, user);

                throw error;
            });

        } catch(error) {
            console.log('Caught AccountDataProvider.updateAccountData error', error);
            throw error;
        }
    }

    private handleFacebookError = async (error, user) => {
        log(`handleFacebookError`, error, user);
        let fbStatus;

        debug(user, 'Caught FacebookDataProvider error (handleFacebookError).', { error });

        const logError = async (error, data) => {
            let errMsg = (error.message ? error.message : error);
            NotificationService.notifyError('Facebook Error', errMsg);
            EventService.dispatch(AppEvents.PROVIDER_API_ERROR, { provider: ProviderNames.FACEBOOK, message: errMsg, error, data });
            await LogService.logError("User is not authenticated throug h Facebook. Error: " + errMsg, data, null, user);
        }

        // needs new authentication?
        if ((error?.code == 190) ||
            (error.message && error.message.indexOf('Missing permissions') >= 0)) {

            fbStatus = await FacebookService.getLoginStatus();

            if (fbStatus?.status == 'connected') {
                console.log(`user is connected, trying to update fb token...`)
                // try to update the token of the current user is the original account holder
                return await FacebookService.tryUpdateToken(fbStatus, user);
            } else {
                console.log(`user is not connected, must login....`)
                // try to login and get a new token...
                if (!await FacebookService.login()) {
                    console.log('FB Login failed, cannot retry.');
                    await logError(error, { error, fbStatus });
                    return false;
                }
                // they logged, in try to repull...
                return true;
            }
        } else {
            console.log('Caught FacebookDataProvider error', error, '\nTrace:');
            //EventService.dispatch(AppEvents.PROVIDER_API_ERROR, { provider: ProviderNames.FACEBOOK, message: "Facebook API Error.", error });
            throw error;
        }
    }

}

export default new FacebookDataProvider();